// src/App.js
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { store } from './store';
import { LanguageProvider } from './contexts/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer'
import Homepage from './components/Homepage';
import { hasPicked, savePicked } from './utils/languagePreference';

// Keep the homepage path in the initial bundle and split every secondary route.
// This prevents map/details/profile code from blocking first load for homepage visitors.
const LayoutDetails = lazy(() => import('./components/LayoutDetails'));
const MapView = lazy(() => import('./components/MapView'));
const Profile = lazy(() => import('./components/Profile'));
const LandList = lazy(() => import('./components/LandList'));
const LandDetailsPage = lazy(() => import('./pages/LandDetailsPage'));
const LikedLandsPage = lazy(() => import('./pages/LikedLandsPage'));
const MyLandsPage = lazy(() => import('./pages/MyLandsPage'));
const OwnerLandsPage = lazy(() => import('./pages/OwnerLandsPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const LanguagePickerModal = lazy(() => import('./components/LanguagePickerModal'));

const RouteFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center" role="status" aria-live="polite">
    <div className="animate-spin rounded-full h-9 w-9 border-2 border-gray-200 border-t-brand-green" />
  </div>
);

// -----------------------------------------------------------------------
// Inner app that can access both LanguageContext and Redux auth
// -----------------------------------------------------------------------
function InnerApp() {
  useEffect(() => {
    import('./services/pwa').then(({ initA2HS }) => {
      try { initA2HS(); } catch { }
    });
  }, []);

  // Analytics is non-critical for rendering. Load it after the first paint so it
  // does not add parsing/execution work to the homepage's critical path.
  useEffect(() => {
    let cancelled = false;
    let idleId;
    let timerId;

    const loadAnalytics = () => {
      import('react-ga4').then(({ default: ReactGA }) => {
        if (cancelled) return;
        ReactGA.initialize('G-4WBE3HZ0GS');
        ReactGA.send({
          hitType: 'pageview',
          page: `${window.location.pathname}${window.location.search}`,
          title: document.title,
        });
      }).catch(() => {});
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(loadAnalytics, { timeout: 1500 });
    } else {
      timerId = window.setTimeout(loadAnalytics, 700);
    }

    return () => {
      cancelled = true;
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      if (timerId) window.clearTimeout(timerId);
    };
  }, []);

  const phone = useSelector((state) => state.auth.phone);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  // Determine whether to show the language picker
  const [showPicker, setShowPicker] = useState(() => {
    // On first render, check if the GUEST key exists (fast path — phone may not be known yet)
    return !hasPicked(null);
  });

  // Once auth resolves, re-check with the user's actual phone key
  useEffect(() => {
    if (isLoggedIn && phone) {
      if (!hasPicked(phone)) {
        // Logged-in user hasn't picked yet → show picker
        setShowPicker(true);
      } else {
        // Already picked as this user → ensure dismissed
        setShowPicker(false);
      }
    }
  }, [isLoggedIn, phone]);

  const handlePickerClose = (pickedLang) => {
    // Save preference: for logged-in users use phone key, otherwise guest key
    if (pickedLang) {
      savePicked(pickedLang, isLoggedIn ? phone : null);
    } else {
      // Dismissed without picking — save current language so we don't re-ask
      savePicked(localStorage.getItem('language') || 'en', isLoggedIn ? phone : null);
    }
    setShowPicker(false);
  };

  const RouteHeader = () => {
    const location = useLocation();
    const isDetails = location.pathname.startsWith('/land-details') || location.pathname.startsWith('/search');
    const isOwnerLands = location.pathname.startsWith('/owner-lands');
    return <Header variant={isOwnerLands ? 'owner' : isDetails ? 'details' : 'default'} />;
  };

  return (
    <Router>
      <div className="App">
        <RouteHeader />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/layout-details/:id" element={<LayoutDetails />} />
            <Route path="/land-details/:id" element={<LandDetailsPage />} />
            <Route path="/likes" element={<LikedLandsPage />} />
            <Route path="/my-lands" element={<MyLandsPage />} />
            <Route path="/owner-lands/:ownerName" element={<OwnerLandsPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/layout-list" element={<h1>Layout List Page</h1>} />
            <Route path="/contact-us" element={<ContactUsPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/map-view" element={<MapView />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/landlist" element={<LandList />} />
          </Routes>
        </Suspense>
        <Footer />

        {/* Language picker — shown once per user / device */}
        {showPicker && (
          <Suspense fallback={null}>
            <LanguagePickerModal onClose={handlePickerClose} />
          </Suspense>
        )}
      </div>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <LanguageProvider>
        <InnerApp />
      </LanguageProvider>
    </Provider>
  );
}

export default App;

